import { ArrowDownUp } from 'lucide-react'
import type { Hex } from 'ox'
import { useState } from 'react'

import { cx } from './core/classes.js'
import * as Currency from './core/currency.js'

/** Toggleable amount display that switches between primary (fiat) and detail (crypto) values. */
export function Amount(props: Amount.Props) {
  const {
    align = 'left',
    amount,
    className,
    maxDecimals,
    precise,
    sign = '',
    size = 'sm',
    strikethrough,
  } = props
  const [showDetail, setShowDetail] = useState(false)
  const signColor =
    sign === '−' || sign === '-' ? 'text-negative' : sign === '+' ? 'text-positive' : undefined
  const primary = Currency.fiat(amount, { maxDecimals, precise })
  const detail = `${sign}${Currency.crypto(amount, { maxDecimals })}`
  const label = showDetail ? detail : `${sign}${primary.label}`
  const title = showDetail ? detail : primary.title ? `${sign}${primary.title}` : label

  return (
    <button
      className={cx(
        'inline-flex min-w-0 max-w-full cursor-pointer items-center focus-visible:outline focus-visible:outline-[2px] focus-visible:outline-offset-[2px] focus-visible:outline-focus',
        Amount.alignClassName[align],
        Amount.sizeClassName[size],
        strikethrough && 'text-foreground-secondary',
        signColor,
        className,
      )}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        if (e.detail !== 0) return
        setShowDetail((s) => !s)
      }}
      onPointerDown={(e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return
        e.preventDefault()
        e.stopPropagation()
        setShowDetail((s) => !s)
      }}
      title={title}
      type="button"
    >
      <span className={cx('flex min-w-0 max-w-full items-center', Amount.labelClassName[size])}>
        <ArrowDownUp className={Amount.iconClassName[size]} />
        <span className={cx('min-w-0 truncate', strikethrough && 'line-through')}>{label}</span>
      </span>
    </button>
  )
}

export namespace Amount {
  export type Amount = {
    /** Token decimals used to format the raw hex amount. */
    decimals: number
    /** Human-readable token amount. */
    formatted: string
    /** Token symbol used for fiat or crypto display. */
    symbol: string
  } & (
    | {
        /** Raw token amount as a hex quantity. */
        amount: Hex.Hex
      }
    | {
        /** Raw token amount as a hex quantity. */
        value: Hex.Hex
      }
  )

  export type Align = 'center' | 'left' | 'right'
  export type Size = 'lg' | 'md' | 'sm'

  export type Token = Amount

  export type Props = {
    /** Horizontal alignment of the value. @default 'left' */
    align?: Align | undefined
    /** Amount object — fiat/crypto formatting is derived automatically. */
    amount: Amount
    /** Additional class names. */
    className?: string | undefined
    /** Maximum fractional digits to show. */
    maxDecimals?: number | undefined
    /** Whether to show nonzero sub-cent fiat values with significant digits. */
    precise?: boolean | undefined
    /** Prefix character displayed before formatted values (e.g. `"−"` or `"+"`). */
    sign?: string | undefined
    /** Size variant. @default 'sm' */
    size?: Size | undefined
    /** Whether to strike through the values (e.g. sponsored fees). Also applies `text-foreground-secondary`. */
    strikethrough?: boolean | undefined
  }

  export const sizeClassName = {
    lg: 'rounded-button px-[12px] py-[4px] heading-32 hover:bg-secondary-hover active:bg-secondary-active',
    md: 'mr-[-6px] rounded-button px-[6px] py-[2px] label-14 hover:bg-secondary-hover active:bg-secondary-active',
    sm: 'mr-[-6px] rounded-button px-[6px] py-[2px] label-13 hover:bg-secondary-hover active:bg-secondary-active',
  } as const

  export const alignClassName = {
    center: 'justify-center text-center',
    left: 'justify-start text-left',
    right: 'justify-end text-right',
  } as const

  export const labelClassName = {
    lg: 'gap-[8px]',
    md: 'gap-[6px]',
    sm: 'gap-[6px]',
  } as const

  export const iconClassName = {
    lg: 'h-[16px] w-[16px] shrink-0 opacity-30',
    md: 'h-[12px] w-[12px] shrink-0 opacity-50',
    sm: 'h-[12px] w-[12px] shrink-0 opacity-50',
  } as const
}
